import { useQueryClient } from "@tanstack/react-query";
import { LoaderCircle, X } from "lucide-react";
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type FormEvent, type KeyboardEvent, type Ref } from "react";
import { useFreigabeEinwilligung, useFreigabeVorschau, useFreigabeWahl, useFreigeben } from "../api/abfragen";
import { ApiFehler, fehlerText } from "../api/client";
import { RUECKFALL_SATZ } from "../api/freigabeRueckfall";
import type { DokumentDetail, FreigabeVorschau } from "../api/typen";
import type { IconTyp } from "../design/Briefgeist";
import { BeispielChip } from "../design/Chip";
import { Feld, Textfeld } from "../design/Feld";
import { Knopf } from "../design/Knopf";
import { Sheet } from "../design/Sheet";
import { useToast } from "../design/toastKontext";
import { FreigabeKurztext } from "./freigabe/FreigabeKurztext";
import { ALLE_OHNE_VORSCHAU, FREIGABE_FASSUNG, FREIGABE_KNOEPFE, FREIGABE_VORBEREITET, NOTIZ } from "./freigabe/kurztext";

/** Grenzen wie der Server (`plan-w81.md` 3.11): hoechstens 20 Begriffe, je 2 bis 80 Zeichen nach dem Trimmen. */
const BEGRIFFE_MAX = 20;
const BEGRIFF_MIN = 2;
const BEGRIFF_MAX = 80;
/** Platzhalter des Servers (E5); die Klammern bleiben im Text, damit er auch vorgelesen erkennbar bleibt. */
const PLATZHALTER = /(\[(?:Name|Anschrift|IBAN|Nummer|E-Mail|Telefon|Geburtsdatum|geschwärzt)\])/;

const zeichen = (t: string) => [...t].length;

/** Ladezeichen am getippten „Freigeben“ der Kurzstufe (P 5), bei reduzierter Bewegung still (Muster Erfassen.tsx). */
const Dreht: IconTyp = (p) => <LoaderCircle {...p} className={`${p.className ?? ""} animate-spin motion-reduce:animate-none`} />;

function stellenText(n: number): string {
  if (n === 0) return "Nichts geschwärzt";
  return n === 1 ? "1 Stelle geschwärzt" : `${n} Stellen geschwärzt`;
}

/** Code eines 409 des Servers (`GEAENDERT`, `FASSUNG`, `SCHON`, `AUS`, `NICHT_MOEGLICH`), sonst null. */
function code409(fehler: unknown): string | null {
  if (!(fehler instanceof ApiFehler) || fehler.status !== 409) return null;
  const c = (fehler.daten as { code?: unknown } | null)?.code;
  return typeof c === "string" ? c : null;
}

/** `dokument` null: Einwilligung fuer die Wahl „Alle meine Briefe“ (Nachtrag D-433), ohne Vorschau eines Briefs.
 *  `rueckfall` (W81 B5, nur ohne Brief): die Fassung der Einwilligung ist neu; der Satz darueber und „Bei einzelnen Briefen
 *  entscheiden“, das `einzeln` ausdruecklich sichert. */
type Props = { dokument: DokumentDetail | null; offen: boolean; onSchliessen: () => void; rueckfall?: boolean };

/** W81 (D-331, `plan-w81.md` 5.2): Blatt „Brief zur Verbesserung freigeben“. Zeigt die Einwilligung und die Vorschau des
 *  Servers, laesst Woerter nachschwaerzen (der Server rechnet, der Client ersetzt nie selbst) und gibt mit „Freigeben“
 *  genau die gezeigte Fassung frei. Gesperrt, solange eine Vorschau oder die Freigabe unterwegs ist (D-089). Ohne Brief
 *  dasselbe Blatt fuer die Einwilligung in „Alle meine Briefe“ (ein Blatt mehr im Inventar waere K3d). */
export function FreigabeSheet({ dokument, offen, onSchliessen, rueckfall = false }: Props) {
  // Zahl der laufenden Anfragen: frei erst, wenn die letzte fertig ist; gesetzt im Tipp, nicht erst einen Takt spaeter
  const [laufend, setLaufend] = useState(0);
  const lauf = useCallback((anfrage: Promise<unknown>) => {
    setLaufend((n) => n + 1);
    void anfrage.catch(() => undefined).finally(() => setLaufend((n) => n - 1));
  }, []);
  // frischer Inhalt je Oeffnen, auch wenn das Blatt noch ausblendet
  const [warOffen, setWarOffen] = useState(offen);
  const [runde, setRunde] = useState(0);
  if (offen !== warOffen) {
    setWarOffen(offen);
    if (offen) setRunde((r) => r + 1);
  }
  return (
    <Sheet
      offen={offen}
      onSchliessen={onSchliessen}
      titel={dokument ? "Brief zur Verbesserung freigeben" : "Alle Briefe zur Verbesserung freigeben"}
      gesperrt={laufend > 0}
    >
      {dokument ? (
        <BriefInhalt key={runde} dokument={dokument} lauf={lauf} laeuft={laufend > 0} onSchliessen={onSchliessen} />
      ) : (
        <AlleInhalt key={runde} lauf={lauf} laeuft={laufend > 0} onSchliessen={onSchliessen} rueckfall={rueckfall} />
      )}
    </Sheet>
  );
}

type Gezeigt = FreigabeVorschau & { begriffe: string[] };
type Stufe = "kurz" | "vorschau";

/** W92 (Nachtrag D-1362 Punkt 14): Satz eines 422 der Notiz. Der Server antwortet als Liste
 *  `{"detail":[{"type":"hinweis","loc":["body","hinweis"],"msg":"…"}]}`; `meldung` (client.ts) machte daraus „Bitte die
 *  Eingaben prüfen.“. Nur der Eintrag mit `type` „hinweis“ zaehlt, sonst null (dann gilt der Satz des Clients, Muster
 *  `serverFehler` in briefe/anschrift.ts). */
function notizSatz(fehler: unknown): string | null {
  if (!(fehler instanceof ApiFehler) || fehler.status !== 422) return null;
  const detail = (fehler.daten as { detail?: unknown } | null)?.detail;
  if (!Array.isArray(detail)) return null;
  const eintrag = (detail as { type?: unknown; msg?: unknown }[]).find((e) => e?.type === "hinweis" && typeof e.msg === "string" && e.msg);
  return eintrag ? String(eintrag.msg) : null;
}

/** W92 (Nachtrag D-1354 Punkt 3, D-1362 Punkt 15): Notiz zum Brief im selben Blatt, mit Zaehler und dem Satz zur Schwaerzung als
 *  Beschreibung des Felds. Enter bricht nur die Zeile um, freigegeben wird allein mit „Freigeben“. */
function NotizFeld({ ref, wert, onWert, nurLesen }: { ref: Ref<HTMLTextAreaElement>; wert: string; onWert: (wert: string) => void; nurLesen: boolean }) {
  return (
    <ul className="gruppe overflow-clip rounded-group bg-pg-surface">
      <Textfeld
        ref={ref}
        label={NOTIZ.label}
        placeholder={NOTIZ.platzhalter}
        rows={3}
        maxLength={NOTIZ.hoechstens}
        readOnly={nurLesen}
        value={wert}
        onChange={(e) => onWert(e.target.value)}
        hinweis={
          <>
            <span className="block">{NOTIZ.hinweis}</span>
            <span className="block tabular-nums">{NOTIZ.zaehler(wert.length)}</span>
          </>
        }
      />
    </ul>
  );
}

function BriefInhalt(props: { dokument: DokumentDetail; lauf: (anfrage: Promise<unknown>) => void; laeuft: boolean; onSchliessen: () => void }) {
  const { dokument, lauf, laeuft, onSchliessen } = props;
  const qc = useQueryClient();
  const toast = useToast();
  const vorschauHolen = useFreigabeVorschau(dokument.id);
  const freigabe = useFreigeben(dokument.id);
  // W92: zuerst die Kurzstufe (Kurztext, drei Knoepfe); „Vorher ansehen“ oder ein 409 GEAENDERT/FASSUNG fuehrt in die Vorschau
  const [stufe, setStufe] = useState<Stufe>("kurz");
  const [begriffe, setBegriffe] = useState<string[]>([]);
  const [eingabe, setEingabe] = useState("");
  const [gezeigt, setGezeigt] = useState<Gezeigt | null>(null);
  const [ladeFehler, setLadeFehler] = useState<unknown>(null);
  const [freigabeFehler, setFreigabeFehler] = useState<unknown>(null);
  const [hinweis, setHinweis] = useState<{ text: string; nr: number } | null>(null);
  // W92: Notiz zum Brief; bleibt beim Wechsel der Stufe und nach 409 und 422 stehen
  const [notizOffen, setNotizOffen] = useState(false);
  const [notiz, setNotiz] = useState("");
  const [notizFehler, setNotizFehler] = useState<{ text: string; nr: number } | null>(null);
  // die Freigabe selbst ist unterwegs (nicht nur eine Vorschau): Ladezeichen am getippten Knopf der Kurzstufe
  const [sendet, setSendet] = useState(false);
  const feld = useRef<HTMLInputElement>(null);
  const hinweisRef = useRef<HTMLParagraphElement>(null);
  const kopf = useRef<HTMLHeadingElement>(null);
  const wartet = useRef<HTMLParagraphElement>(null);
  const notizRef = useRef<HTMLTextAreaElement>(null);
  // Fokus nach dem Wechsel in die Vorschau-Stufe auf „Was wir bekommen“ (P 6), nach dem Aufklappen ins Notizfeld
  const kopfFokus = useRef(false);
  const notizFokus = useRef(false);
  // laufende Nummer je Vorschau: nur die Antwort der juengsten Anfrage wird gezeigt
  const nummer = useRef(0);
  // ein POST …/freigabe je Tipp, auch bei Doppeltipp und gehaltener Taste
  const riegel = useRef(false);
  const gestartet = useRef(false);
  // W92 (S78-9): mit fester Fassung kommt 409 FASSUNG nur von einem Server ohne W92-S; nur der erste fuehrt in eine neue
  // Vorschau, jeder weitere zeigt die Fehlerzeile (keine Schleife aus Vorschau und Freigabe)
  const fassungAbgelehnt = useRef(false);

  const holen = useCallback(
    (liste: string[]) => {
      const nr = ++nummer.current;
      const anfrage = vorschauHolen.mutateAsync(liste);
      lauf(anfrage);
      anfrage.then(
        (v) => {
          if (nr !== nummer.current) return;
          setGezeigt({ ...v, begriffe: liste });
          setLadeFehler(null);
        },
        (e: unknown) => {
          if (nr === nummer.current) setLadeFehler(e);
        },
      );
    },
    [vorschauHolen, lauf],
  );

  // beim Oeffnen genau eine Vorschau ohne eigene Begriffe (StrictMode ruft den Effekt zweimal)
  useEffect(() => {
    if (gestartet.current) return;
    gestartet.current = true;
    holen([]);
  }, [holen]);

  useEffect(() => {
    if (hinweis) hinweisRef.current?.focus();
  }, [hinweis]);

  // nach dem Zeichnen, bevor Radix (focus-scope) oder die Fokuswache des Blatts den Fokus vom weggefallenen Knopf holt
  useLayoutEffect(() => {
    if (notizFokus.current && notizRef.current) {
      notizFokus.current = false;
      notizRef.current.focus();
    }
    if (!kopfFokus.current) return;
    if (kopf.current) {
      kopfFokus.current = false;
      kopf.current.focus();
    } else wartet.current?.focus();
  });

  const begriff = eingabe.trim();
  const voll = begriffe.length >= BEGRIFFE_MAX;
  const gueltig = zeichen(begriff) >= BEGRIFF_MIN && zeichen(begriff) <= BEGRIFF_MAX && !voll;

  // Der Knopf ist waehrend einer Vorschau gesperrt (damit auch Enter im Feld, der Browser sendet ohne freien Knopf nicht);
  // kommt doch eine zweite Vorschau dazu (Chip entfernt), zeigt das Blatt nur die Antwort der juengsten
  function schwaerzen(e: FormEvent) {
    e.preventDefault();
    if (!gueltig) return;
    setEingabe("");
    feld.current?.focus();
    if (begriffe.some((b) => b.toLocaleLowerCase("de") === begriff.toLocaleLowerCase("de"))) return;
    const neu = [...begriffe, begriff];
    setBegriffe(neu);
    holen(neu);
  }

  function entfernen(weg: string) {
    const neu = begriffe.filter((b) => b !== weg);
    setBegriffe(neu);
    holen(neu);
    feld.current?.focus();
  }

  function vorherAnsehen() {
    kopfFokus.current = true;
    setStufe("vorschau");
  }

  function notizAufklappen() {
    notizFokus.current = true;
    setNotizOffen(true);
  }

  /** W92 (Nachtrag D-1354 Punkt 9): eine Freigabe fuer beide Stufen. Die Kurzstufe gibt die Vorschau beim Oeffnen frei (ohne
   *  eigene Woerter, dieselbe Kennung), die Vorschau-Stufe die gezeigte mit den Woertern der Chips; die Fassung ist immer
   *  `FREIGABE_FASSUNG`. Die Notiz geht nur mit, wenn sie mehr als Leerraum enthaelt, sonst fehlt das Feld ganz. */
  function freigeben(quelle: Stufe) {
    if (riegel.current || laeuft || !gezeigt || ladeFehler || freigabeFehler) return;
    riegel.current = true;
    setHinweis(null);
    setNotizFehler(null);
    const v = gezeigt;
    const liste = quelle === "kurz" ? [] : v.begriffe;
    const text = notiz.trim();
    const anfrage = freigabe.mutateAsync({ fassung: FREIGABE_FASSUNG, begriffe: liste, kennung: v.kennung, ...(text ? { hinweis: text } : {}) });
    lauf(anfrage);
    setSendet(true);
    anfrage.then(
      () => {
        toast("Freigegeben. Danke!");
        onSchliessen();
      },
      (e: unknown) => {
        riegel.current = false;
        setSendet(false);
        const code = code409(e);
        const satz = notizSatz(e);
        if (satz) setNotizFehler((h) => ({ text: satz, nr: (h?.nr ?? 0) + 1 }));
        else if (code === "GEAENDERT" || (code === "FASSUNG" && !fassungAbgelehnt.current)) {
          if (code === "FASSUNG") fassungAbgelehnt.current = true;
          // neue Vorschau mit denselben Begriffen, der Satz des Servers ueber „Freigeben“; aus der Kurzstufe in die Vorschau
          setStufe("vorschau");
          holen(liste);
          setHinweis((h) => ({ text: fehlerText(e), nr: (h?.nr ?? 0) + 1 }));
        } else if (code === "SCHON") {
          void qc.invalidateQueries({ queryKey: ["dokument", dokument.id] });
          toast(fehlerText(e));
          onSchliessen();
        } else setFreigabeFehler(e);
      },
    );
  }

  function neuLaden() {
    if (laeuft) return;
    setFreigabeFehler(null);
    // Verifier R1: die Begriffe der Chips; nach einer gescheiterten Vorschau mit neuem Begriff traegt `gezeigt` noch die alte
    // Liste, der Chip stuende sonst ohne Schwaerzung im Text und ginge mit „Freigeben“ nicht hinaus
    holen(begriffe);
  }

  const fehler = ladeFehler ?? freigabeFehler;
  const tasteHalten = (k: KeyboardEvent) => {
    if (k.repeat && k.key === "Enter") k.preventDefault();
  };
  const notizZeile = notizOffen ? <NotizFeld ref={notizRef} wert={notiz} onWert={setNotiz} nurLesen={sendet} /> : null;
  const notizMeldung = notizFehler && (
    <p key={notizFehler.nr} role="alert" className="t-subheadline mt-4 px-4 text-pg-danger">
      {notizFehler.text}
    </p>
  );

  if (stufe === "kurz") {
    const frei = Boolean(gezeigt) && !laeuft && !fehler;
    return (
      <>
        <FreigabeKurztext />
        <div className="mt-6 flex flex-col gap-3">
          <Knopf gross breit disabled={sendet} onClick={vorherAnsehen}>
            {FREIGABE_KNOEPFE.vorher}
          </Knopf>
          <Knopf
            art="sekundaer"
            breit
            disabled={!frei}
            aria-busy={sendet || undefined}
            icon={sendet ? Dreht : undefined}
            onKeyDown={tasteHalten}
            onClick={() => freigeben("kurz")}
          >
            {FREIGABE_KNOEPFE.freigeben}
          </Knopf>
          {notizZeile ?? (
            <Knopf art="sekundaer" breit disabled={!frei} onClick={notizAufklappen}>
              {FREIGABE_KNOEPFE.mitFeedback}
            </Knopf>
          )}
        </div>
        {notizMeldung}
        {fehler ? (
          <Fehler fehler={fehler} laeuft={laeuft} onNeu={neuLaden} />
        ) : (
          !gezeigt && (
            <p role="status" className="t-subheadline mt-4 px-4 text-pg-text-2">
              {FREIGABE_VORBEREITET}
            </p>
          )
        )}
      </>
    );
  }

  if (!gezeigt) {
    return ladeFehler ? (
      <Fehler fehler={ladeFehler} laeuft={laeuft} onNeu={neuLaden} />
    ) : (
      <p ref={wartet} role="status" tabIndex={-1} className="t-subheadline px-4 py-6 text-pg-text-2 outline-none">
        Vorschau wird erstellt …
      </p>
    );
  }

  return (
    <>
      {/* W92: der Kurztext statt der Absaetze des Servers (`gezeigt.einwilligung`), vor „Freigeben“ */}
      <FreigabeKurztext />
      <h3 ref={kopf} tabIndex={-1} className="t-headline mt-6 px-4 outline-none">
        Was wir bekommen
      </h3>
      {gezeigt.angaben.length > 0 && (
        <ul aria-label="Angaben" className="gruppe mt-2 overflow-clip rounded-group bg-pg-surface">
          {/* wie `Eigenschaft` (Gruppe.tsx), die Namensspalte aber hoechstens so breit wie die Zeile: im Blatt ist die Karte
              bei 320 px und 200 % schmaler als die Spalte (grosseSchrift „ragt ueber den Kartenrahmen“) */}
          {gezeigt.angaben.map((a, i) => (
            <li key={`${a.name}-${i}`} className="zeile relative flex min-h-(--pg-hit) flex-wrap items-baseline gap-x-4 gap-y-1 px-4 py-3">
              <span className="t-body w-28 max-w-full shrink-0 text-pg-text-2">{a.name}</span>
              <span className="fliesstext t-body min-w-0 break-words">{a.wert}</span>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={schwaerzen} aria-label="Weitere Stelle schwärzen" className="mt-4">
        <ul className="gruppe overflow-clip rounded-group bg-pg-surface">
          <Feld ref={feld} label="Wort oder Zahl" autoComplete="off" value={eingabe} maxLength={BEGRIFF_MAX} onChange={(e) => setEingabe(e.target.value)} />
        </ul>
        <div className="mt-2 flex justify-end">
          <Knopf art="sekundaer" type="submit" disabled={!gueltig || laeuft}>
            Schwärzen
          </Knopf>
        </div>
      </form>
      {begriffe.length > 0 && (
        <ul aria-label="Selbst geschwärzt" className="mt-2 flex flex-wrap gap-2 px-4">
          {begriffe.map((b) => (
            <li key={b} className="max-w-full">
              <BeispielChip aria-label={`„${b}“ nicht mehr schwärzen`} onClick={() => entfernen(b)} className="gap-1 break-words">
                <span className="min-w-0 break-words">{b}</span>
                <X size={16} strokeWidth={1.75} aria-hidden className="shrink-0" />
              </BeispielChip>
            </li>
          ))}
        </ul>
      )}
      <p role="status" className="t-footnote mt-4 px-4 text-pg-text-2">
        {stellenText(gezeigt.stellen)}
      </p>
      <div className="mt-2 rounded-group bg-pg-surface px-4 py-3">
        <div data-testid="freigabe.text" className="t-body whitespace-pre-wrap break-words">
          {gezeigt.text.split(PLATZHALTER).map((teil, i) =>
            i % 2 ? (
              <mark key={i} className="rounded-xs bg-pg-surface-2 text-pg-text">
                {teil}
              </mark>
            ) : (
              teil
            ),
          )}
        </div>
      </div>
      {/* W92: die Notiz-Zeile ueber „Freigeben“, zugeklappt als leiser Knopf, aufgeklappt mit dem Feld */}
      <div className="mt-4">
        {notizZeile ?? (
          <Knopf art="text" disabled={laeuft} onClick={notizAufklappen}>
            {FREIGABE_KNOEPFE.mitFeedback}
          </Knopf>
        )}
      </div>
      {hinweis && (
        <p key={hinweis.nr} ref={hinweisRef} role="alert" tabIndex={-1} className="t-subheadline mt-4 px-4 text-pg-danger outline-none">
          {hinweis.text}
        </p>
      )}
      {Boolean(fehler) && <Fehler fehler={fehler} laeuft={laeuft} onNeu={neuLaden} />}
      {notizMeldung}
      <Knopf gross breit className="mt-6" disabled={laeuft || Boolean(fehler)} onKeyDown={tasteHalten} onClick={() => freigeben("vorschau")}>
        Freigeben
      </Knopf>
    </>
  );
}

/** Nachtrag D-433: Einwilligung in „Alle meine Briefe“ mit dem Text des Servers; „Alle freigeben“ setzt die Wahl mit der
 *  gezeigten Fassung. Ist die Fassung inzwischen eine andere (409 `FASSUNG`), kommt der neue Text mit dem Satz darueber.
 *  W81 B5: im Rueckfall zuerst der Satz zur neuen Fassung, unter „Alle freigeben“ „Bei einzelnen Briefen entscheiden“
 *  (`einzeln` mit `fassung: null`, der Server loescht die Marke); das geht auch, wenn der Text nicht kommt. */
function AlleInhalt(props: { lauf: (anfrage: Promise<unknown>) => void; laeuft: boolean; onSchliessen: () => void; rueckfall: boolean }) {
  const { lauf, laeuft, onSchliessen, rueckfall } = props;
  const toast = useToast();
  const einwilligung = useFreigabeEinwilligung(true);
  const wahl = useFreigabeWahl();
  const [fehler, setFehler] = useState<unknown>(null);
  const [hinweis, setHinweis] = useState<{ text: string; nr: number } | null>(null);
  const hinweisRef = useRef<HTMLParagraphElement>(null);
  const riegel = useRef(false);
  const ohneVorschauId = useId();
  useEffect(() => {
    if (hinweis) hinweisRef.current?.focus();
  }, [hinweis]);
  const text = einwilligung.data;
  const holt = einwilligung.isFetching;
  // W82 Web-8 W8-4 (WN-108, wie iOS NO-1): der Hinweis „ohne Vorschau“ steht immer vor der Bestätigung, beim Laden, bei einem
  // Fehler und im Rückfall, und beschreibt „Alle freigeben“
  const ohneVorschau = (
    <p id={ohneVorschauId} className="t-body mt-4 hyphens-auto break-words px-4" data-testid="freigabe.ohne-vorschau">
      {ALLE_OHNE_VORSCHAU}
    </p>
  );

  function alleFreigeben() {
    if (riegel.current || laeuft || holt || !text || fehler) return;
    riegel.current = true;
    setHinweis(null);
    const anfrage = wahl.mutateAsync({ wahl: "alle", fassung: text.fassung });
    lauf(anfrage);
    anfrage.then(
      () => {
        toast("Gesichert.");
        onSchliessen();
      },
      (e: unknown) => {
        riegel.current = false;
        if (code409(e) === "FASSUNG") {
          void einwilligung.refetch();
          setHinweis((h) => ({ text: fehlerText(e), nr: (h?.nr ?? 0) + 1 }));
        } else setFehler(e);
      },
    );
  }

  function einzelnEntscheiden() {
    if (riegel.current || laeuft || fehler) return;
    riegel.current = true;
    setHinweis(null);
    const anfrage = wahl.mutateAsync({ wahl: "einzeln", fassung: null });
    lauf(anfrage);
    anfrage.then(
      () => {
        toast("Gesichert.");
        onSchliessen();
      },
      (e: unknown) => {
        riegel.current = false;
        setFehler(e);
      },
    );
  }

  const laden = einwilligung.error ? (
    <Fehler fehler={einwilligung.error} laeuft={holt} onNeu={() => void einwilligung.refetch()} />
  ) : (
    <p role="status" className="t-subheadline px-4 py-6 text-pg-text-2">
      Wird geladen …
    </p>
  );
  if (!text && !rueckfall)
    return (
      <>
        {ohneVorschau}
        {laden}
      </>
    );
  const einzeln = rueckfall && (
    <Knopf
      art="text"
      breit
      className="mt-3 hyphens-auto break-words"
      disabled={laeuft || Boolean(fehler)}
      onKeyDown={(k) => {
        if (k.repeat && k.key === "Enter") k.preventDefault();
      }}
      onClick={einzelnEntscheiden}
    >
      Bei einzelnen Briefen entscheiden
    </Knopf>
  );
  const satz = rueckfall && <p className="t-body mt-4 hyphens-auto break-words px-4">{RUECKFALL_SATZ}</p>;
  if (!text)
    return (
      <>
        {satz}
        {ohneVorschau}
        {fehler ? <Fehler fehler={fehler} laeuft={laeuft} onNeu={() => setFehler(null)} /> : laden}
        {einzeln}
      </>
    );
  return (
    <>
      {satz}
      {ohneVorschau}
      {text.einwilligung.map((absatz, i) => (
        <p key={i} className={`t-body hyphens-auto break-words px-4 text-pg-text-2 ${i === 0 && !rueckfall ? "mt-4" : "mt-3"}`}>
          {absatz}
        </p>
      ))}
      <h3 className="t-headline mt-6 px-4">Was wir bekommen</h3>
      <p className="t-body mt-2 hyphens-auto break-words px-4 text-pg-text-2">
        Von jedem Brief an dich, der ab jetzt abgelegt wird, auch von anderen im Haushalt, und von jedem Brief ohne Person, den du
        ablegst: den erkannten Text, in dem Namen, Anschriften, IBAN, Nummern, Mailadressen und Telefonnummern durch Platzhalter
        ersetzt sind, und die Einordnung mit Kategorie, Art, Datum, Betrag und Fristen.
      </p>
      {hinweis && (
        <p key={hinweis.nr} ref={hinweisRef} role="alert" tabIndex={-1} className="t-subheadline mt-4 px-4 text-pg-danger outline-none">
          {hinweis.text}
        </p>
      )}
      {Boolean(fehler) && (
        <Fehler
          fehler={fehler}
          laeuft={laeuft || holt}
          onNeu={() => {
            setFehler(null);
            void einwilligung.refetch();
          }}
        />
      )}
      <Knopf
        gross
        breit
        className="mt-6"
        disabled={laeuft || holt || Boolean(fehler)}
        aria-describedby={ohneVorschauId}
        onKeyDown={(k) => {
          if (k.repeat && k.key === "Enter") k.preventDefault();
        }}
        onClick={alleFreigeben}
      >
        Alle freigeben
      </Knopf>
      {einzeln}
    </>
  );
}

/** Satz des Servers oder „Keine Verbindung …“ (nie die Meldung des Browsers) mit „Noch einmal laden“. */
function Fehler(props: { fehler: unknown; laeuft: boolean; onNeu: () => void }) {
  return (
    <div className="mt-4 flex flex-col items-start gap-1">
      <p role="alert" className="t-subheadline px-4 text-pg-danger">
        {fehlerText(props.fehler)}
      </p>
      <Knopf art="text" disabled={props.laeuft} onClick={props.onNeu}>
        Noch einmal laden
      </Knopf>
    </div>
  );
}
